import EventEmitter from 'events';
import { BlockchainTransactionType } from '../types/web3-types';
import { ethers } from 'ethers';
import { isUserOpResponse } from '@towns/userops';
export class BlockchainTransactionStore extends EventEmitter {
    transactions = {};
    spaceDapp;
    promiseQueue = [];
    abortController = new AbortController();
    constructor(spaceDapp) {
        super();
        this.spaceDapp = spaceDapp;
    }
    begin({ type, data }) {
        const id = `STORE_ID__${ethers.Wallet.createRandom().address}`;
        const tx = {
            id,
            type,
            data,
            status: 'potential',
        };
        this.updateTransaction(tx);
        return ({ hashOrUserOpHash, error, transaction, eventListener, }) => {
            if (hashOrUserOpHash && transaction) {
                this.moveToPending(id, {
                    hashOrUserOpHash,
                    transaction,
                    type,
                    data,
                    eventListener,
                });
                return;
            }
            if (error) {
                if (this.transactions[id].status !== 'failure') {
                    this.updateTransaction({
                        ...tx,
                        status: 'failure',
                        error: error,
                    });
                }
            }
        };
    }
    async stop() {
        this.spaceDapp.provider?.removeAllListeners(); // remove all pending txs
        this.abortController.abort();
        await Promise.all(this.promiseQueue);
        this.promiseQueue = [];
        this.transactions = {};
    }
    moveToPending(id, tx) {
        this.updateTransaction({
            ...tx,
            id,
            status: 'pending',
        });
        const _promise = new Promise((resolve) => {
            const blockchainListener = async () => {
                this.abortController.signal.addEventListener('abort', () => {
                    return resolve();
                });
                const streamId = tx.data?.spaceStreamId;
                const type = tx.type;
                try {
                    const transactionOrUserOp = tx.transaction;
                    let hashToWaitFor = tx.hashOrUserOpHash;
                    // wait for event to be emitted
                    if (tx.eventListener) {
                        const result = await tx.eventListener.wait();
                        if (this.abortController.signal.aborted) {
                            return;
                        }
                        this.updateTransaction({
                            ...tx,
                            id,
                            status: result.success ? 'success' : 'failure',
                            receipt: result.receipt,
                        });
                        return;
                    }
                    else {
                        if (isUserOpResponse(transactionOrUserOp)) {
                            const userOpEvent = await transactionOrUserOp.wait();
                            if (this.abortController.signal.aborted) {
                                return;
                            }
                            // TODO: parse user operation error
                            if (!userOpEvent || userOpEvent.args.success === false) {
                                this.updateTransaction({
                                    ...tx,
                                    id,
                                    status: 'failure',
                                });
                                return;
                            }
                            hashToWaitFor = userOpEvent.transactionHash;
                        }
                        // this should be the same wait time as townsClient.waitForTransaction
                        // only waiting for tx.wait() can resolve quicker than provider.waitForTransaction
                        await this.spaceDapp.provider?.waitForTransaction(hashToWaitFor);
                        if (this.abortController.signal.aborted) {
                            return;
                        }
                        this.updateTransaction({
                            ...tx,
                            id,
                            status: 'success',
                        });
                    }
                }
                catch (error) {
                    const isWalletLink = type === BlockchainTransactionType.LinkWallet ||
                        type === BlockchainTransactionType.UnlinkWallet;
                    const parsedError = isWalletLink
                        ? this.spaceDapp.getWalletLink().parseError(error)
                        : this.spaceDapp.parseSpaceError(streamId ?? '', error);
                    this.updateTransaction({
                        ...tx,
                        id,
                        status: 'failure',
                        error: parsedError,
                    });
                }
                finally {
                    // Remove the promise from the queue after it resolves or is aborted
                    this.promiseQueue = this.promiseQueue.filter((p) => p !== _promise);
                    resolve();
                }
            };
            void blockchainListener();
        });
        this.promiseQueue.push(_promise);
    }
    updateTransaction(tx) {
        this.transactions[tx.id] = {
            ...this.transactions[tx.id],
            ...tx,
        };
        this.emit('updatedTransaction', this.transactions[tx.id]);
        this.emit('transactions', this.transactions);
    }
}
//# sourceMappingURL=BlockchainTransactionStore.js.map